import { useMemo, useState } from "react";
import { Alert, Card, Input, Select, Switch, Tooltip } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { SupplierDto } from "@audit-plan/shared";
import dayjs from "dayjs";
import { useAuth } from "../auth/AuthContext";
import { ApiError, download } from "../api/client";
import { useSuppliers } from "../api/suppliers";
import { useShowRisk } from "../api/plan";
import { Pill, Rating, RiskPill, fmtD, fmtN } from "../components/pills";
import { PageHead } from "../components/dashboard/Kpi";
import { FilterBar, PageActions } from "../components/FilterBar";
import { ResponsiveList } from "../components/ResponsiveList";
import { FactoryStatusTag } from "../components/factory/FactoryTable";

/** Stato VendorHub del fornitore: il peggiore dei suoi stabilimenti; se sono diversi, il dettaglio nel tooltip (AUD-F8.1). */
function VendorStatus({ s }: { s: SupplierDto }) {
  if (!s.factoryStatus) return <>—</>;
  const all = Object.entries(s.factoryStatuses ?? {});
  const tag = <FactoryStatusTag s={s.factoryStatus} />;
  if (all.length < 2) return tag;
  return (
    <Tooltip title={all.map(([k, n]) => `${k}: ${n}`).join(" · ")}>
      <span style={{ whiteSpace: "nowrap" }}>{tag} <span style={{ fontSize: 12, opacity: 0.65 }}>+{all.length - 1}</span></span>
    </Tooltip>
  );
}

export default function SuppliersPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [risk, setRisk] = useState<string>();
  const [category, setCategory] = useState<string>();
  const [po, setPo] = useState<string>();
  // disattivati (RAT26-F3.1b): solo l'admin, con l'interruttore
  const [inactive, setInactive] = useState(false);
  const res = useSuppliers(can("admin") && inactive ? { includeInactive: true } : undefined);
  const showRisk = useShowRisk();
  const all = res.data ?? [];
  const cats = useMemo(() => [...new Set(all.map((s) => s.category).filter(Boolean))].sort(), [all]);
  const list = all
    .filter((s) => {
      const n = q.trim().toLowerCase();
      return (
        (!n || [s.name, s.code, s.vat, s.category].join(" ").toLowerCase().includes(n)) &&
        (!risk || s.risk === risk) &&
        (!category || s.category === category) &&
        (!po || (po === "yes" ? s.phaseOut : !s.phaseOut))
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  if (res.error) return <Alert type="error" showIcon message={res.error instanceof ApiError ? res.error.message : t("a_loadFail")} />;
  const year = dayjs().year();

  return (
    <div>
      <PageHead
        title={t("suppliers")}
        actions={
          <PageActions
            items={[
              { key: "xlsx", label: t("exportXlsx"), icon: <DownloadOutlined />, onClick: () => void download("/exports/suppliers.xlsx") },
            ]}
          />
        }
      />
      <FilterBar
        active={[risk, category, po].filter(Boolean).length}
        onReset={() => { setRisk(undefined); setCategory(undefined); setPo(undefined); }}
        search={<Input.Search allowClear style={{ width: 280, maxWidth: "100%" }} placeholder={`${t("name")}, ${t("vat")}…`} onChange={(e) => setQ(e.target.value)} />}
      >
        {showRisk && <Select allowClear placeholder={t("risk")} style={{ width: 150 }} value={risk} onChange={setRisk} options={["high", "medium", "low"].map((r) => ({ value: r, label: t(`risk_${r}`) }))} />}
        <Select allowClear showSearch placeholder={t("category")} style={{ width: 220 }} value={category} onChange={setCategory} options={cats.map((c) => ({ value: c, label: c }))} />
        <Select allowClear placeholder={t("phaseout")} style={{ width: 150 }} value={po} onChange={setPo} options={[{ value: "yes", label: t("ans_Y") }, { value: "no", label: t("ans_N") }]} />
        {can("admin") && (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Switch size="small" checked={inactive} onChange={setInactive} /> {t("act_showInactive")}
          </label>
        )}
      </FilterBar>
      <Card styles={{ body: { padding: 0 } }}>
        <ResponsiveList<SupplierDto>
          rowKey="id"
          loading={res.isLoading}
          dataSource={list}
          emptyText={all.length ? t("noResults") : t("emptyStore")}
          pageSize={25}
          onRowClick={(s) => nav(`/suppliers/${s.id}`)}
          rowStyle={(s) => (s.phaseOut ? { background: "#EED8E022" } : undefined)}
          tableProps={{ rowClassName: (s) => (s.phaseOut ? "row-phaseout" : "") }}
          card={{
            code: (s) => `${s.code}${s.category ? ` · ${s.category}` : ""}`,
            title: (s) => s.name,
            subtitle: (s) => `${s.country}${s.vat ? ` · ${s.vat}` : ""}`,
            tags: (s) => (
              <>
                {s.phaseOut && <Pill tone="crit">{t("phaseout")}</Pill>}
                {!s.active && <Pill tone="mute">{t("act_inactive")}</Pill>}
                {s.factoryStatus && <VendorStatus s={s} />}
                {showRisk && <RiskPill r={s.risk} />}
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginRight: 4 }}>
                  <Rating score={s.rating} override={s.ratingOverride} /> <span style={{ fontSize: 12, opacity: 0.65 }}>{fmtN(s.rating, 1)}</span>
                </span>
                {showRisk && !s.phaseOut && <Pill tone={(s.visitsPlannedThisYear ?? 0) >= (s.visitsRequired ?? 0) ? "ok" : "bad"}>{`${year}: ${s.visitsPlannedThisYear ?? 0}/${s.visitsRequired ?? 0}`}</Pill>}
              </>
            ),
            fields: (s) => [
              [t("lastAudit"), fmtD(s.lastAuditDate)],
              [t("openNc"), s.openNcs || "—"],
              [t("capPct"), s.capPct == null ? "—" : `${s.capPct}%`],
            ],
          }}
          columns={[
            { title: t("sapCode"), dataIndex: "code", render: (v: string) => <span style={{ fontFamily: "var(--ap-mono)" }}>{v}</span> },
            {
              title: t("name"),
              render: (_, s) => (
                <div>
                  <b>{s.name}</b> {s.phaseOut && <Pill tone="crit">{t("phaseout")}</Pill>} {!s.active && <Pill tone="mute">{t("act_inactive")}</Pill>}
                  <div style={{ fontSize: 12, opacity: 0.65 }}>
                    {s.country}
                    {s.vat ? ` · ${s.vat}` : ""}
                  </div>
                </div>
              ),
            },
            { title: t("f_factories"), dataIndex: "factoryCount", align: "right" as const, render: (v?: number) => v ?? "—" },
            // stato VendorHub del fornitore, finora solo nel dettaglio (review di Pier, AUD-F8.1)
            { title: t("f_status"), dataIndex: "factoryStatus", render: (_: unknown, s: SupplierDto) => <VendorStatus s={s} /> },
            { title: t("category"), dataIndex: "category" },
            ...(showRisk ? [{ title: t("risk"), dataIndex: "risk", render: (v: string) => <RiskPill r={v} /> }] : []),
            {
              title: t("rating"),
              render: (_: unknown, s: SupplierDto) => (
                <span>
                  <Rating score={s.rating} override={s.ratingOverride} /> <span style={{ fontSize: 12, opacity: 0.65 }}>{fmtN(s.rating, 1)}</span>
                </span>
              ),
            },
            { title: t("lastAudit"), dataIndex: "lastAuditDate", render: (v?: string | null) => fmtD(v) },
            { title: t("openNc"), dataIndex: "openNcs", align: "right" as const, render: (v?: number) => v || "—" },
            { title: t("capPct"), dataIndex: "capPct", align: "right" as const, render: (v?: number | null) => (v == null ? "—" : `${v}%`) },
            ...(showRisk
              ? [
                  {
                    title: `${t("visitsPlanned")} ${year}`,
                    align: "center" as const,
                    render: (_: unknown, s: SupplierDto) => {
                      if (s.phaseOut) return "—";
                      const p = s.visitsPlannedThisYear ?? 0;
                      const r = s.visitsRequired ?? 0;
                      return <Pill tone={p >= r ? "ok" : "bad"}>{`${p} / ${r}`}</Pill>;
                    },
                  },
                ]
              : []),
          ]}
        />
      </Card>
    </div>
  );
}
